
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Services() {
  useEffect(() => {
    // Add Schema.org JSON-LD for services page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services#service`,
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`,
      "name": "Palm Oil Industry Services - Mill Setup, Export & OEM Manufacturing",
      "description": "Comprehensive Malaysian palm oil services including mill & refinery setup, premium export services, and OEM contract manufacturing with government certifications.",
      "provider": {
        "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`
      },
      "serviceType": "Industrial Consulting and Manufacturing",
      "areaServed": [
        "Malaysia", "Asia", "Middle East", "Africa", "Europe", "Oceania"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Palm Oil Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Palm Oil Mill & Refinery Setup",
              "description": "Complete consultation and setup services for palm oil mills and refineries"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Palm Oil Exporting",
              "description": "Premium quality palm oil export services with CP6, CP8, CP10 grades"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "OEM Contract Manufacturing",
              "description": "Custom branding and private label manufacturing services"
            }
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-building-line',
      title: 'Palm Oil Mill & Refinery Setup',
      description: 'Complete end-to-end consultation and setup services for palm oil mills and refineries with state-of-the-art technology and equipment.',
      features: [
        'Feasibility studies and site assessment',
        'Equipment selection and procurement',
        'Installation supervision and commissioning',
        'Staff training and knowledge transfer',
        'Ongoing technical support and maintenance'
      ],
      benefits: [
        'Reduced setup time by 40%',
        'Optimized operational efficiency',
        'Compliance with international standards',
        'Cost-effective solutions'
      ]
    },
    {
      icon: 'ri-ship-line',
      title: 'Palm Oil Exporting',
      description: 'Premium quality palm oil products with flexible packaging options, serving international markets with reliable supply chains.',
      features: [
        'CP6, CP8, CP10 grade specifications',
        'Bulk and containerized shipping',
        'Custom packaging solutions',
        'Quality assurance and testing',
        'Documentation and certification support'
      ],
      benefits: [
        'Consistent product quality',
        'Competitive pricing',
        'Reliable delivery schedules',
        'Full traceability'
      ]
    },
    {
      icon: 'ri-settings-3-line',
      title: 'OEM Contract Manufacturing',
      description: 'Custom branding solutions for your palm oil products with our established manufacturing expertise and quality control systems.',
      features: [
        'Private label manufacturing',
        'Custom packaging design',
        'Quality control and testing',
        'Regulatory compliance assistance',
        'Supply chain management'
      ],
      benefits: [
        'Reduced manufacturing costs',
        'Faster time to market',
        'Brand differentiation',
        'Scalable production capacity'
      ]
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'We start with a comprehensive consultation to understand your specific needs, objectives, and constraints.',
      icon: 'ri-discuss-line'
    },
    {
      step: '02',
      title: 'Assessment & Planning',
      description: 'Our experts conduct detailed assessments and develop customized solutions tailored to your requirements.',
      icon: 'ri-search-line'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'We execute the project with precision, ensuring quality standards and timelines are met throughout.',
      icon: 'ri-tools-line'
    },
    {
      step: '04',
      title: 'Support & Maintenance',
      description: 'Ongoing support and maintenance services to ensure optimal performance and continuous improvement.',
      icon: 'ri-customer-service-line'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-80 sm:h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/0ecbdd6a86b1f7778fef88cb85deca5c.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
          <p className="text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto px-4">
            Malaysian expertise delivering comprehensive palm oil solutions for global success
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6">Our Services</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive palm oil industry solutions backed by Malaysian expertise and government certifications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
            {services.map((service, index) => (
              <div key={index} className="bg-yellow-50 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col lg:flex-row items-start space-y-4 lg:space-y-0 lg:space-x-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-yellow-600`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6">{service.description}</p>
                    
                    <div className="mb-6">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Key Features</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <i className="ri-check-line text-yellow-600 mt-1 text-sm"></i>
                            <span className="text-gray-600 text-sm sm:text-base">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mb-6">
                      <div className="h-40 sm:h-48 overflow-hidden rounded-xl">
                        {index === 0 && (
                          <img 
                            src="https://readdy.ai/api/search-image?query=Modern%20Malaysian%20palm%20oil%20refinery%20manufacturing%20plant%20with%20large%20industrial%20processing%20equipment%2C%20palm%20oil%20mill%20factory%20with%20refining%20machinery%20and%20tanks%2C%20large%20scale%20palm%20oil%20processing%20facility%20in%20Malaysia%20with%20modern%20industrial%20infrastructure&width=800&height=400&seq=palm-refinery-factory&orientation=landscape"
                            alt="Palm Oil Refinery Manufacturing Plant"
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        )}
                        {index === 1 && (
                          <img 
                            src="https://readdy.ai/api/search-image?query=International%20shipping%20port%20with%20palm%20oil%20containers%20being%20loaded%20for%20export%2C%20Malaysian%20palm%20oil%20shipping%20terminal%20with%20cargo%20containers%20and%20port%20logistics%2C%20large%20shipping%20vessels%20loading%20palm%20oil%20products%20at%20industrial%20port%20facility&width=800&height=400&seq=palm-export-port&orientation=landscape"
                            alt="Palm Oil Export Shipping Port"
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        )}
                        {index === 2 && (
                          <div className="h-full bg-white p-2 flex items-center justify-center">
                            <img 
                              src="https://static.readdy.ai/image/5c4d3156b18a91d26c6b2b068b2e3a44/d2d3ff7b1a8de1176a43e83f325d2c99.jfif"
                              alt="OEM Contract Manufacturing"
                              className="max-w-full max-h-full object-contain"
                              loading="lazy"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Link to="/contact">
                      <Button className="w-full text-sm sm:text-base">Learn More</Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-yellow-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">Our Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach that ensures successful project delivery and client satisfaction
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-24 h-24 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${step.icon} text-3xl text-white`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-12 h-12 bg-white border-4 border-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-yellow-600">{step.step}</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6">Why Choose Our Services?</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <i className="ri-medal-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">Proven Expertise</h4>
                    <p className="text-sm sm:text-base text-gray-600">Over 10 years of experience with 50+ successfully completed projects across multiple continents.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <i className="ri-shield-check-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">Quality Assurance</h4>
                    <p className="text-sm sm:text-base text-gray-600">Rigorous quality control processes and compliance with international standards and certifications.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <i className="ri-customer-service-2-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">Dedicated Support</h4>
                    <p className="text-sm sm:text-base text-gray-600">24/7 technical support and ongoing maintenance services to ensure optimal performance.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">Cost-Effective Solutions</h4>
                    <p className="text-sm sm:text-base text-gray-600">Competitive pricing with transparent costs and no hidden fees, delivering maximum value for investment.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative order-1 lg:order-2">
              <div className="h-80 sm:h-96 bg-gray-50 rounded-2xl shadow-2xl flex items-center justify-center p-4">
                <img 
                  src="https://static.readdy.ai/image/5c4d3156b18a91d26c6b2b068b2e3a44/49cabe9aaa41e585238ef710e0e6b84f.jfif"
                  alt="MATRADE Malaysian Palm Oil Event"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Standards */}
      <section className="py-20 bg-yellow-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">Industry Standards &amp; Certifications</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We comply with the highest international standards to ensure quality and safety
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {['JAKIM Halal', 'MPOB', 'MOH Malaysia', 'RSPO', 'Export Permit'].map((cert, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="ri-award-line text-3xl text-yellow-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">{cert}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-yellow-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 px-4">Partner with Malaysian Excellence</h2>
          <p className="text-lg sm:text-xl mb-8 max-w-3xl mx-auto px-4">
            Contact us today for a free consultation and discover how our Malaysian expertise and government certifications can accelerate your business success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <Link to="/contact">
              <Button variant="secondary" size="lg" className="bg-white text-yellow-600 hover:bg-gray-100 w-full sm:w-auto">
                <i className="ri-phone-line mr-2"></i>
                Get Free Consultation
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-yellow-600 w-full sm:w-auto">
                <i className="ri-product-hunt-line mr-2"></i>
                View Our Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
